import { useSyncExternalStore } from 'react'
import { recapConfigDefaults, type RecapConfig } from '../data/recapConfig'
import type { BeeArchetypeId, RecapModule } from '../types/engagement'

/**
 * In-memory recap configuration shared by Mobile and Admin (prototype).
 * Resets on reload. REQUIRES BACKEND (Marco 3): serve, version and authorize the config.
 */
let current: RecapConfig = structuredCloneConfig(recapConfigDefaults)
const listeners = new Set<() => void>()

function structuredCloneConfig(config: RecapConfig): RecapConfig {
  return JSON.parse(JSON.stringify(config)) as RecapConfig
}

export function getRecapConfig(): RecapConfig {
  return current
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Live recap configuration; re-renders when Admin changes a parameter. */
export function useRecapConfig(): RecapConfig {
  return useSyncExternalStore(subscribe, getRecapConfig, getRecapConfig)
}

const numericKeys = [
  'shareRewardBee',
  'minCommunityPopulation',
  'minSocialInteractions',
  'archetypeTieTolerancePct',
  'archetypeBalancedSpreadPct',
] as const

/**
 * Apply one Admin edit. Param ids:
 * `<numericKey>` · `modules.<module>` · `archetypes.<id>.<name|description>.<locale>`.
 */
export function setRecapParam(id: string, raw: string): void {
  const next = structuredCloneConfig(current)
  const [head, a, b, c] = id.split('.')
  if ((numericKeys as readonly string[]).includes(head)) {
    const value = Number(raw)
    if (!Number.isFinite(value) || value < 0) return
    next[head as (typeof numericKeys)[number]] = value
  } else if (head === 'modules' && a) {
    next.modules[a as RecapModule] = raw === 'true'
  } else if (head === 'archetypes' && a && (b === 'name' || b === 'description') && (c === 'pt-BR' || c === 'en-US')) {
    const entry = next.archetypes[a as BeeArchetypeId]
    if (!entry || raw.trim() === '') return
    entry[b][c] = raw.trim()
  } else {
    return
  }
  current = next
  listeners.forEach((listener) => listener())
}
